import { PLAYER_CONFIG } from "./GameConfig";
import { WEAPON_CONFIGS, type WeaponKind } from "../weapons/Weapon";
import type { LevelMode } from "../generation/types";

export type GamePhase = "menu" | "loading" | "playing" | "paused" | "dead" | "complete";

export interface WeaponAmmo {
  magazine: number;
  reserve: number;
}

export class GameState {
  public phase: GamePhase = "menu";
  public health: number = PLAYER_CONFIG.maximumHealth;
  public armor: number = 0;
  public selectedWeapon: WeaponKind = "pistol";
  public hasShotgun = false;
  public hasRifle = false;
  public readonly ammo: Record<WeaponKind, WeaponAmmo> = {
    rifle: { magazine: 0, reserve: 0 },
    pistol: { magazine: WEAPON_CONFIGS.pistol.magazineSize, reserve: 36 },
    shotgun: { magazine: 0, reserve: 0 },
  };
  public kills = 0;
  public totalEnemies = 0;
  public secrets = 0;
  public totalSecrets = 1;
  public elapsedSeconds = 0;
  public mode: LevelMode = "procedural";

  public constructor(public seed: string) {}

  public reset(
    seed: string,
    enemyCount: number,
    mode: LevelMode = "procedural",
    secretCount = 1,
  ): void {
    this.seed = seed;
    this.phase = "playing";
    this.health = PLAYER_CONFIG.maximumHealth;
    this.armor = 0;
    this.selectedWeapon = "pistol";
    this.hasShotgun = false;
    this.hasRifle = false;
    this.ammo.rifle = { magazine: 0, reserve: 0 };
    this.ammo.pistol = { magazine: WEAPON_CONFIGS.pistol.magazineSize, reserve: 36 };
    this.ammo.shotgun = { magazine: 0, reserve: 0 };
    this.kills = 0;
    this.totalEnemies = enemyCount;
    this.secrets = 0;
    this.elapsedSeconds = 0;
    this.mode = mode;
    this.totalSecrets = secretCount;
  }

  public addHealth(amount: number): boolean {
    if (this.health >= PLAYER_CONFIG.maximumHealth) {
      return false;
    }
    this.health = Math.min(PLAYER_CONFIG.maximumHealth, this.health + amount);
    return true;
  }

  public addArmor(amount: number): boolean {
    if (this.armor >= 100) {
      return false;
    }
    this.armor = Math.min(100, this.armor + amount);
    return true;
  }

  public addAmmo(weapon: WeaponKind, amount: number): boolean {
    const ammo = this.ammo[weapon];
    const maximum = weapon === "pistol" ? 120 : weapon === "rifle" ? 240 : 48;
    if (ammo.reserve >= maximum) {
      return false;
    }
    ammo.reserve = Math.min(maximum, ammo.reserve + amount);
    return true;
  }

  public grantShotgun(): boolean {
    if (this.hasShotgun) {
      return this.addAmmo("shotgun", 6);
    }
    this.hasShotgun = true;
    this.selectedWeapon = "shotgun";
    this.ammo.shotgun.magazine = WEAPON_CONFIGS.shotgun.magazineSize;
    this.ammo.shotgun.reserve = 12;
    return true;
  }

  public get ownedWeapons(): WeaponKind[] {
    return ["pistol" as const, ...(this.hasShotgun ? ["shotgun" as const] : []),
      ...(this.hasRifle ? ["rifle" as const] : [])];
  }

  public grantRifle(): boolean {
    if (this.hasRifle) return this.addAmmo("rifle", 30);
    this.hasRifle = true;
    this.selectedWeapon = "rifle";
    this.ammo.rifle = { magazine: WEAPON_CONFIGS.rifle.magazineSize, reserve: 60 };
    return true;
  }
}
